import {
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr,
} from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZoneChangeDetection,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import {
  AuthGuardAutoLogin,
  AuthService,
  Identity,
  setupAuth,
} from '@nx-vigil/core/web/auth/feature';
import {
  CORE_ENVIRONMENT_DEFAULTS,
  CORE_SERVICE,
  EnvironmentStore,
} from '@nx-vigil/core/web/environment/data-access';
import {
  CORE_ORG_PRODUCT_LOGO,
  CORE_PROFILE_USER,
} from '@nx-vigil/core/web/shell/ui';
import { themePreset } from '@nx-vigil/core/web/theme/ui';
import {
  BASE_PATH,
  CoreService,
} from '@nx-vigil/identity/web/generated/api-client';
import { getRoutes } from '@nx-vigil/identity/web/shell/feature';
import {
  provideTanStackQuery,
  QueryClient,
} from '@tanstack/angular-query-experimental';
import { withDevtools } from '@tanstack/angular-query-experimental/devtools';
import { OAuthModule } from 'angular-oauth2-oidc';
import { LoggerModule, NGXLogger } from 'ngx-logger';
import { provideOptimus } from '@openng/optimus-ui/config';
import { environment } from '../environment';
import { setupLogging } from '../initializers/logging';
import { provideFeatureRoutes } from './feature-routes';

export function createConfig(
  identity: Identity,
  features: { [key: string]: boolean },
): ApplicationConfig {
  return {
    providers: [
      provideBrowserGlobalErrorListeners(),
      provideZoneChangeDetection({ eventCoalescing: true }),
      { provide: BASE_PATH, useValue: `${location.origin}/api` },
      { provide: CORE_SERVICE, useClass: CoreService },
      {
        provide: CORE_PROFILE_USER,
        useFactory: () => {
          const authService = inject(AuthService);
          return toSignal(authService.user$);
        },
      },
      { provide: CORE_ENVIRONMENT_DEFAULTS, useValue: environment },
      { provide: CORE_ORG_PRODUCT_LOGO, useValue: environment.productLogo },
      provideHttpClient(withXhr(), withInterceptorsFromDi()),
      importProvidersFrom(
        LoggerModule.forRoot(null),
        OAuthModule.forRoot({
          resourceServer: {
            allowedUrls: [`${location.origin}/api`],
            sendAccessToken: true,
          },
        }),
      ),
      AuthGuardAutoLogin,
      provideAppInitializer(() => {
        const initializerFn = setupAuth(identity, inject(AuthService));
        return initializerFn();
      }),
      provideTanStackQuery(
        new QueryClient(),
        withDevtools(() => ({
          loadDevtools: features['tanstack-devtools'] ?? 'auto',
        })),
      ),
      provideAppInitializer(() => {
        const initializerFn = setupLogging(
          inject(EnvironmentStore),
          inject(NGXLogger),
        );
        return initializerFn();
      }),
      provideAnimationsAsync(),
      provideOptimus({
        theme: {
          preset: themePreset,
          options: { darkModeSelector: '.dark' },
        },
      }),
      provideFeatureRoutes(() => {
        const environmentStore = inject(EnvironmentStore);
        const authService = inject(AuthService);
        return {
          features$: environmentStore.features$,
          getRoutes,
          refreshFeatures: () => environmentStore.refresh(),
          isAuthenticated$: authService.isAuthenticated$,
        };
      }),
    ],
  };
}
